import {
    buildView,
    emptyText,
    isOverdue,
    rowLabel,
    standingLabel,
    titleLabel,
    whereLabel,
    type HireReturnConfig,
    type HireReturnRow,
} from "./hireReturnModel";

export interface HireReturnRenderContext {
    emitClick(intent: unknown, rowScope?: Record<string, unknown>): void;
    cellClickIntent?: unknown;
    localize?(text: string | null | undefined): string;
    editMode?: boolean;
    permissionDenied?: boolean;
}

export const MyHireReturnsWidgetKey = "KeyView.MyHireReturns";
export const ProjectHireReturnsWidgetKey = "KeyView.ProjectHireReturns";

/**
 * The hire-return card. It reuses the check-reminder card's CSS classes (`keyview-check-reminders`,
 * `reminder-*`) so the reminders read as one family on a dashboard, with `reminder-item-overdue` marking an
 * item that should already have gone back.
 */
export class HireReturnWidget {
    readonly widgetTypeKey: string;

    private host: HTMLElement | null = null;
    private titleEl: HTMLElement | null = null;
    private bodyEl: HTMLElement | null = null;
    private ctx: HireReturnRenderContext | null = null;

    constructor(widgetTypeKey: string = MyHireReturnsWidgetKey) {
        this.widgetTypeKey = widgetTypeKey;
    }

    render(host: HTMLElement, config: HireReturnConfig, data: unknown, ctx: HireReturnRenderContext): void {
        this.host = host;
        this.ctx = ctx;
        host.innerHTML = "";

        const root = document.createElement("div");
        root.className = "keyview-check-reminders keyview-hire-returns";

        this.titleEl = document.createElement("div");
        this.titleEl.className = "title";
        this.bodyEl = document.createElement("div");
        this.bodyEl.className = "reminder-body";

        root.appendChild(this.titleEl);
        root.appendChild(this.bodyEl);
        host.appendChild(root);

        this.draw(config, data);
    }

    update(config: HireReturnConfig, data: unknown): void {
        if (this.bodyEl) this.draw(config, data);
    }

    dispose(): void {
        if (this.host) this.host.innerHTML = "";
        this.host = this.titleEl = this.bodyEl = null;
        this.ctx = null;
    }

    private text(value: string | null | undefined): string {
        const localize = this.ctx?.localize;
        return localize ? localize(value) : (value ?? "");
    }

    private draw(config: HireReturnConfig, data: unknown): void {
        const body = this.bodyEl;
        if (!body) return;

        if (this.titleEl) this.titleEl.textContent = this.text(titleLabel(config));
        body.innerHTML = "";

        const view = buildView(data);

        if (view.total === 0) {
            const empty = document.createElement("div");
            empty.className = "reminder-empty";
            empty.textContent = this.text(emptyText(config));
            body.appendChild(empty);
            return;
        }

        const list = document.createElement("ul");
        list.className = "reminder-list";
        for (const row of view.rows)
            list.appendChild(this.drawRow(row, config));
        body.appendChild(list);

        const summary = document.createElement("div");
        summary.className = "reminder-summary";
        summary.textContent = view.overdue > 0
            ? `${view.total} on the list · ${view.overdue} overdue`
            : `${view.total} on the list`;
        body.appendChild(summary);
    }

    private drawRow(row: HireReturnRow, config: HireReturnConfig): HTMLElement {
        const li = document.createElement("li");
        li.className = `reminder-item ${isOverdue(row) ? "reminder-item-overdue" : "reminder-item-duesoon"}`;
        li.dataset.lineId = String(row.LineId);
        li.dataset.standing = row.Standing ?? "";

        const title = document.createElement("span");
        title.className = "reminder-title";
        title.textContent = this.text(rowLabel(row));
        title.title = title.textContent;
        li.appendChild(title);

        const meta = document.createElement("span");
        meta.className = "reminder-meta";

        if (config?.ShowProject !== false) {
            const project = document.createElement("span");
            project.className = row.ProjectId == null ? "reminder-project reminder-project-none" : "reminder-project";
            project.textContent = this.text(whereLabel(row));
            meta.appendChild(project);
        }

        if (config?.ShowResponsible !== false && row.ResponsibleContactName) {
            const responsible = document.createElement("span");
            responsible.className = "reminder-responsible";
            responsible.textContent = this.text(row.ResponsibleContactName);
            meta.appendChild(responsible);
        }

        li.appendChild(meta);

        const due = document.createElement("span");
        due.className = "reminder-due";
        due.textContent = this.text(standingLabel(row));
        li.appendChild(due);

        const intent = this.ctx?.cellClickIntent;
        if (intent && !this.ctx?.editMode && !this.ctx?.permissionDenied) {
            li.classList.add("is-clickable");
            li.style.cursor = "pointer";
            li.addEventListener("click", () => this.ctx?.emitClick(intent, {
                ItemId: row.ItemId,
                AgreementId: row.AgreementId ?? null,
                LineId: row.LineId,
                ProjectId: row.ProjectId ?? null,
                Standing: row.Standing ?? null,
            }));
        }

        return li;
    }
}
